import { useQuery, useMutation } from '@tanstack/react-query';

/** Shape returned by GET /auth/register/policy */
export interface RegisterPolicy {
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireDigit: boolean;
    requireSpecial: boolean;
  };
  /** Countries the BE accepts for registration — shared constant (currently ~5 entries). */
  supportedCountries: { code: string; name: string; flag_emoji?: string }[];
  /**
   * Current terms version string — sent as `acceptedTermsVersion` in POST /auth/register.
   * Never hardcode this value; always use policy.termsVersion.
   */
  termsVersion: string;
  /** URL or path to the current terms document — may be null if not configured */
  termsUrl: string | null;
}

/** Body sent to POST /auth/register */
export interface RegisterRequest {
  email: string;
  password: string;
  pocName: string;
  /**
   * E.164 format — e.g. "+14155550123". OPTIONAL: the signup form collects no phone number,
   * so it is normally omitted. RegisterDto marks it @IsOptional() (self-signup-r2 T01) and
   * leaves `poc_contact_number` null when absent; a provided-but-malformed value still 400s.
   */
  phone?: string;
  companyName: string;
  /** Company website — optional, sent when provided in Step 2 */
  website?: string;
  /** Must be a code present in RegisterPolicy.supportedCountries */
  countryCode: string;
  /** From policy.termsVersion — never a hardcoded literal */
  acceptedTermsVersion: string;
  /** Organisation type — determines onboarding fork after registration.
   * Wire values are uppercase to match the persona vocabulary already in the BE seed/ACL. */
  orgType: 'CLIENT' | 'AGENCY';
}

/** 202 Accepted response from POST /auth/register — identical for new AND existing email */
export interface RegisterResponse {
  registrationId: string;
  message: string;
}

export interface RegisterFieldError {
  field: string;
  message: string;
}

export class RegisterApiError extends Error {
  readonly status: number;
  readonly code: 'field_validation' | 'rate_limited' | 'unavailable' | 'unknown';
  readonly retryAfterSecs: number | undefined;
  readonly fieldErrors: RegisterFieldError[] | undefined;

  constructor(
    status: number,
    code: 'field_validation' | 'rate_limited' | 'unavailable' | 'unknown',
    retryAfterSecs?: number,
    fieldErrors?: RegisterFieldError[],
  ) {
    super(`Registration failed: ${code} (HTTP ${status})`);
    this.name = 'RegisterApiError';
    this.status = status;
    this.code = code;
    this.retryAfterSecs = retryAfterSecs;
    this.fieldErrors = fieldErrors;
  }
}

// Use the same gateway origin resolution as rest_client.ts — must be absolute so
// the standalone SignupApp (which runs before Keycloak, outside the Vite proxy /api
// prefix) reaches the gateway directly rather than hitting the Vite dev server.
const GATEWAY_URL =
  (import.meta.env.VITE_GATEWAY_URL as string | undefined) ??
  (import.meta.env.VITE_GRAPHQL_URL as string | undefined)?.replace(/\/graphql\/?$/, '') ??
  'http://localhost:4000';

const POLICY_URL = `${GATEWAY_URL}/auth/register/policy`;
const REGISTER_URL = `${GATEWAY_URL}/auth/register`;

/**
 * GET /auth/register/policy — public, no Authorization header.
 * Returns password rules, supported country list, and current terms version.
 */
export async function fetchRegisterPolicy(): Promise<RegisterPolicy> {
  const res = await fetch(POLICY_URL);
  if (!res.ok) throw new RegisterApiError(res.status, 'unknown');
  // Gateway returns {terms:{version,url}} (nested). Tolerant fallback keeps working
  // if a future BE normalises to flat termsVersion/termsUrl.
  const data = await res.json();
  return {
    ...data,
    termsVersion: data.terms?.version ?? data.termsVersion,
    termsUrl: data.terms?.url ?? data.termsUrl ?? null,
  } as RegisterPolicy;
}

/**
 * POST /auth/register — public, no Authorization header.
 * Resolves to RegisterResponse on 202.
 * Throws RegisterApiError on 400/429/503.
 *
 * SECURITY: 202 is returned for BOTH new and already-registered emails.
 * The caller must never branch on the response to distinguish the two cases.
 */
export async function postRegister(body: RegisterRequest): Promise<RegisterResponse> {
  const res = await fetch(REGISTER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (res.status === 202) return res.json() as Promise<RegisterResponse>;

  if (res.status === 400) {
    const data: { errors?: RegisterFieldError[] } = await res.json().catch(() => ({}));
    throw new RegisterApiError(400, 'field_validation', undefined, data.errors ?? []);
  }

  if (res.status === 429) {
    const retryAfter = Number(res.headers.get('Retry-After') ?? 60);
    throw new RegisterApiError(429, 'rate_limited', retryAfter);
  }

  if (res.status === 503) {
    throw new RegisterApiError(503, 'unavailable');
  }

  throw new RegisterApiError(res.status, 'unknown');
}

/** Fetch password policy + country list + terms version on mount. No auth needed. */
export function useRegisterPolicy() {
  return useQuery({
    queryKey: ['auth', 'register-policy'],
    queryFn: fetchRegisterPolicy,
    staleTime: 5 * 60 * 1000,
  });
}

/** Submit the registration form. Resolves to RegisterResponse on 202. */
export function useRegister() {
  return useMutation({
    mutationFn: (req: RegisterRequest) => postRegister(req),
  });
}
